// src/pages/temples/TempleOrderPage.jsx
// หน้าจัดเรียงลำดับวัดแบบลากวาง (แยกตามขอบเขตสิทธิ์ ภาค/จังหวัด/อำเภอ/ตำบล)
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, message, Tag, Input, Select, Space, Alert, Popconfirm, Typography, Spin, Empty } from 'antd';
import { SaveOutlined, UndoOutlined, HolderOutlined, ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  scopeKeyFromParts,
  buildOrderMap,
  effectiveOrder,
} from '../../utils/templeOrder';

const { Text } = Typography;

const TYPE_COLORS = {
  'พระอารามหลวง': 'gold',
  'วิสุงคามสีมา': 'blue',
  'พัทธสีมา': 'cyan',
  'วัดราษฎร์': 'green',
  'ที่พักสงฆ์': 'orange',
};

const thaiCollator = new Intl.Collator('th', { sensitivity: 'base' });

function SortableTempleRow({ temple, index, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: temple.id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    marginBottom: 8,
    background: '#fff',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 10 : undefined,
    boxShadow: isDragging ? '0 6px 16px rgba(0,0,0,0.2)' : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: disabled ? 'not-allowed' : 'grab',
          color: disabled ? '#d9d9d9' : '#8c8c8c',
          fontSize: 18,
          padding: '0 4px',
        }}
        title={disabled ? 'ปิดการค้นหาเพื่อลากวาง' : 'ลากเพื่อย้าย'}
      >
        <HolderOutlined />
      </span>
      <span style={{ width: 36, textAlign: 'center', fontWeight: 'bold' }}>{index + 1}</span>
      <span style={{ flex: 1, fontWeight: 600 }}>{temple.name}</span>
      <Tag color={TYPE_COLORS[temple.type] || 'default'}>{temple.type || '-'}</Tag>
      <span style={{ color: '#8c8c8c', fontSize: 13 }}>
        {temple.province} {temple.district} {temple.subdistrict}
      </span>
    </div>
  );
}

const TempleOrderPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [allTemples, setAllTemples] = useState([]);
  const [orderRows, setOrderRows] = useState([]);
  const [scope, setScope] = useState({ type: 'ภาค', province: '', district: '', subdistrict: '' });
  const [items, setItems] = useState([]); // วัดเรียงตามลำดับปัจจุบัน (ทั้งขอบเขต)
  const [originalIds, setOriginalIds] = useState([]);
  const [searchText, setSearchText] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // ---- โหลดข้อมูลเริ่มต้น ----
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [profileRes, templesRes, orderRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/users/me`, { headers }),
          axios.get(`${API_BASE_URL}/api/get-temples`),
          axios.get(`${API_BASE_URL}/api/temple-order`, { headers }),
        ]);
        const p = { ...profileRes.data };
        setProfile(p);
        setAllTemples(templesRes.data.temples || []);
        setOrderRows(orderRes.data.orders || []);
        // ขอบเขตเริ่มต้น = ของผู้ใช้ (สร้างตามระดับอย่างถูกต้อง: จังหวัด = province, อำเภอ = province+district, ตำบล = ครบ)
        if (p.level === 'ตำบล') {
          setScope({ type: 'ตำบล', province: p.province || '', district: p.district || '', subdistrict: p.subdistrict || '' });
        } else if (p.level === 'อำเภอ') {
          setScope({ type: 'อำเภอ', province: p.province || '', district: p.district || '', subdistrict: '' });
        } else if (p.level === 'จังหวัด') {
          setScope({ type: 'จังหวัด', province: p.province || '', district: '', subdistrict: '' });
        } else {
          setScope({ type: 'ภาค', province: '', district: '', subdistrict: '' });
        }
      } catch (err) {
        message.error('ไม่สามารถโหลดข้อมูลได้');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const orderMap = useMemo(() => buildOrderMap(orderRows), [orderRows]);
  const scopeKey = useMemo(() => scopeKeyFromParts(scope), [scope]);

  // กรองวัดตามขอบเขตที่เลือก
  const scopedTemples = useMemo(() => {
    return allTemples.filter((t) => {
      if (scope.type === 'ตำบล') {
        return t.province === scope.province && t.district === scope.district && t.subdistrict === scope.subdistrict;
      }
      if (scope.type === 'อำเภอ') {
        return t.province === scope.province && t.district === scope.district;
      }
      if (scope.type === 'จังหวัด') {
        return t.province === scope.province;
      }
      return true;
    });
  }, [allTemples, scope]);

  // เรียงตามลำดับที่ใช้จริง (effective order) เมื่อขอบเขต/ลำดับที่บันทึกเปลี่ยน
  useEffect(() => {
    const ordered = effectiveOrder(scopedTemples, scopeKey, orderMap);
    setItems(ordered);
    setOriginalIds(ordered.map((t) => t.id));
  }, [scopedTemples, scopeKey, orderMap]);

  const hasCustomOrder = orderMap.has(scopeKey);
  const dirty = items.some((t, i) => t.id !== originalIds[i]);

  // ---- ตัวเลือกขอบเขตสำหรับระดับ ภาค ----
  const provinceOptions = useMemo(
    () =>
      [...new Set(allTemples.map((t) => t.province).filter(Boolean))]
        .sort((a, b) => thaiCollator.compare(a, b))
        .map((v) => ({ value: v, label: v })),
    [allTemples]
  );
  const districtOptions = useMemo(
    () =>
      scope.province
        ? [...new Set(allTemples.filter((t) => t.province === scope.province).map((t) => t.district).filter(Boolean))]
            .sort((a, b) => thaiCollator.compare(a, b))
            .map((v) => ({ value: v, label: v }))
        : [],
    [allTemples, scope.province]
  );
  const subdistrictOptions = useMemo(
    () =>
      scope.province && scope.district
        ? [
            ...new Set(
              allTemples
                .filter((t) => t.province === scope.province && t.district === scope.district)
                .map((t) => t.subdistrict)
                .filter(Boolean)
            ),
          ]
            .sort((a, b) => thaiCollator.compare(a, b))
            .map((v) => ({ value: v, label: v }))
        : [],
    [allTemples, scope.province, scope.district]
  );

  const updateScope = (patch) => {
    const next = { ...scope, ...patch };
    let type = 'ภาค';
    if (next.province) type = 'จังหวัด';
    if (next.province && next.district) type = 'อำเภอ';
    if (next.province && next.district && next.subdistrict) type = 'ตำบล';
    setScope({ type, province: next.province || '', district: next.district || '', subdistrict: next.subdistrict || '' });
  };

  const scopeLabel = useMemo(() => {
    if (!profile) return '';
    if (profile.level === 'ภาค') {
      if (scope.type === 'ภาค') return 'ทั่วทั้งภาค';
      return [scope.province, scope.district, scope.subdistrict].filter(Boolean).join(' · ');
    }
    return [profile.province, profile.district, profile.subdistrict].filter(Boolean).join(' · ');
  }, [profile, scope]);

  // ---- ค้นหา (ปิดการลากวางเมื่อค้นหา เพื่อไม่ให้สลับรายการโดยไม่ตั้งใจ) ----
  const searchActive = searchText.trim().length > 0;
  const displayItems = useMemo(() => {
    if (!searchActive) return items;
    const kw = searchText.trim().toLowerCase();
    return items.filter((t) =>
      `${t.name} ${t.province} ${t.district} ${t.subdistrict}`.toLowerCase().includes(kw)
    );
  }, [items, searchText, searchActive]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((t) => t.id === active.id);
      const newIndex = prev.findIndex((t) => t.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API_BASE_URL}/api/temple-order`,
        {
          scope_type: scope.type,
          scope_province: scope.province,
          scope_district: scope.district,
          scope_subdistrict: scope.subdistrict,
          templeIds: items.map((t) => t.id),
        },
        { headers }
      );
      message.success('บันทึกลำดับเรียบร้อยแล้ว');
      setOriginalIds(items.map((t) => t.id));
      const res = await axios.get(`${API_BASE_URL}/api/temple-order`, { headers });
      setOrderRows(res.data.orders || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'บันทึกลำดับไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_BASE_URL}/api/temple-order/reset`,
        {
          scope_type: scope.type,
          scope_province: scope.province,
          scope_district: scope.district,
          scope_subdistrict: scope.subdistrict,
        },
        { headers }
      );
      message.success('รีเซ็ตเป็นลำดับมาตรฐานแล้ว');
      const res = await axios.get(`${API_BASE_URL}/api/temple-order`, { headers });
      setOrderRows(res.data.orders || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'รีเซ็ตไม่สำเร็จ');
    }
  };

  return (
    <Spin spinning={loading}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/temples')}>
          กลับ
        </Button>
        <h1 style={{ margin: 0 }}>จัดเรียงลำดับวัด (ลากวาง)</h1>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Text strong>ขอบเขตที่กำลังจัด:</Text>
            {profile?.level === 'ภาค' ? (
              <>
                <Select
                  placeholder="เลือกจังหวัด (ทั้งหมด = ทั้งภาค)"
                  allowClear
                  value={scope.province || undefined}
                  style={{ width: 220 }}
                  options={provinceOptions}
                  onChange={(v) => updateScope({ province: v || '', district: '', subdistrict: '' })}
                  showSearch
                />
                {scope.province && (
                  <Select
                    placeholder="เลือกอำเภอ (ทั้งหมด)"
                    allowClear
                    value={scope.district || undefined}
                    style={{ width: 220 }}
                    options={districtOptions}
                    onChange={(v) => updateScope({ district: v || '', subdistrict: '' })}
                    showSearch
                  />
                )}
                {scope.province && scope.district && (
                  <Select
                    placeholder="เลือกตำบล (ทั้งหมด)"
                    allowClear
                    value={scope.subdistrict || undefined}
                    style={{ width: 220 }}
                    options={subdistrictOptions}
                    onChange={(v) => updateScope({ subdistrict: v || '' })}
                    showSearch
                  />
                )}
              </>
            ) : (
              <Text>{scopeLabel}</Text>
            )}
            <Tag color="blue">{scopedTemples.length} วัด</Tag>
          </Space>
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message={
              !hasCustomOrder
                ? 'ขอบเขตนี้ยังไม่ได้จัดลำดับ — กำลังแสดงลำดับมาตรฐาน (หรือลำดับของระดับย่อยที่จัดไว้) ลากวางแล้วกด "บันทึกลำดับ" เพื่อเริ่มต้น'
                : 'ขอบเขตนี้มีลำดับที่จัดไว้แล้ว — ลากวางเพื่อปรับ แล้วกด "บันทึกลำดับ"'
            }
          />
        </Space>
      </Card>

      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <Input.Search
          placeholder="ค้นหา ชื่อวัด จังหวัด อำเภอ ตำบล (ปิดการค้นหาเพื่อลากวาง)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: '100%', maxWidth: 380 }}
          allowClear
        />
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!dirty}
            onClick={handleSave}
          >
            บันทึกลำดับ
          </Button>
          <Popconfirm
            title="รีเซ็ตเป็นลำดับมาตรฐาน?"
            description="ลำดับที่จัดไว้ในขอบเขตนี้จะถูกลบทั้งหมด"
            onConfirm={handleReset}
            okText="รีเซ็ต"
            okButtonProps={{ danger: true }}
            cancelText="ยกเลิก"
          >
            <Button icon={<UndoOutlined />} disabled={saving || !hasCustomOrder}>
              รีเซ็ตเป็นมาตรฐาน
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {searchActive && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="กำลังค้นหาอยู่ — ปิดการค้นหาเพื่อลากวาง (การค้นหาไม่เปลี่ยนลำดับที่บันทึก)"
        />
      )}

      {displayItems.length === 0 ? (
        <Empty description="ไม่พบวัดในขอบเขตนี้" style={{ marginTop: 32 }} />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={displayItems.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            {displayItems.map((t) => (
              <SortableTempleRow
                key={t.id}
                temple={t}
                index={items.findIndex((x) => x.id === t.id)}
                disabled={searchActive}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </Spin>
  );
};

export default TempleOrderPage;